import { X } from 'lucide-react';
import { useState, useEffect } from 'react';

interface AIPromptOverlayProps {
  projectId: string;
  onDismiss: () => void;
  onStartChat: () => void;
}

export function AIPromptOverlay({ projectId, onDismiss, onStartChat }: AIPromptOverlayProps) {
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if user previously dismissed for this session
    const key = `overlay-dismissed-${projectId}`;
    const wasDismissed = sessionStorage.getItem(key);
    if (wasDismissed) {
      setDismissed(true);
    }
  }, [projectId]);

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem(`overlay-dismissed-${projectId}`, 'true');
    onDismiss();
  };

  if (dismissed) return null;

  return (
    <div className="absolute inset-0 bg-background/95 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="max-w-2xl mx-auto p-8 text-center relative">
        <button
          onClick={handleDismiss}
          className="absolute top-0 right-0 p-2 hover:bg-muted rounded-md"
          aria-label="Dismiss"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="space-y-6">
          <div className="space-y-2">
            <h2 className="text-3xl font-bold">Ready to Build?</h2>
            <p className="text-lg text-muted-foreground">
              Describe what you want to create, and I'll help you build it step by step.
            </p>
          </div>

          <div className="bg-muted/50 rounded-lg p-6 space-y-4">
            <p className="text-sm text-muted-foreground font-medium">
              Try saying something like:
            </p>
            <div className="space-y-2 text-left">
              <p className="text-sm">• "Create a todo app with dark mode"</p>
              <p className="text-sm">• "Build a portfolio site with a hero section"</p>
              <p className="text-sm">• "Make a dashboard with charts and data tables"</p>
            </div>
          </div>

          <button
            onClick={() => {
              handleDismiss();
              onStartChat();
            }}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
          >
            Start Building with AI
          </button>

          <button
            onClick={handleDismiss}
            className="block mx-auto text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            I'll explore on my own
          </button>
        </div>
      </div>
    </div>
  );
}
